import { useState } from 'react';
import { useFeatureUser, useQueryFetch } from 'sonata-react-components';

import KpiOverview from '@/components/driveToStore/common/KpiOverview/KpiOverview';
import ReportHeader from '@/components/driveToStore/common/ReportHeader/ReportHeader';
import TrafficAnalysis from '@/components/driveToStore/common/TrafficAnalysis/TrafficAnalysis';
import PoiSelectionModal from '@/components/driveToStore/PoiSelectionModal/PoiSelectionModal';
import {
  getAggregatedFootfallLineItemData,
  getFootfallPopularTimes,
  getFootfallStats,
  getPoiList
} from '@/services/footfall.service';
import { BuyerInsights } from './components/BuyerInsights/BuyerInsights';
import PopularTimes from './components/PopularTimes/PopularTimes';
import StoreHeader from './components/StoreHeader/StoreHeader';
import FootfallReportSkeleton from './FootfallReportSkeleton';

import type {
  DayKey,
  KpiOverview as kpiOverViewType,
  PopularTimes as PopularTimesType
} from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';
import type { AggregatedFootfallLineItemDataResponse, FootfallStatsResponse } from '@/services/footfall.service';
import type { Poi, PopularTimesData } from '@/types/footfall';

import './footfallReport.scss';

const DAYS_ORDER: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function mapPopularTimes(popularTimes: PopularTimesType): PopularTimesData {
  const days = DAYS_ORDER.map(dayKey => {
    const scores = popularTimes.busyness[dayKey] ?? [];
    const openingHours = popularTimes.openingHours[dayKey] ?? null;
    const firstNonZeroIndex = scores.findIndex((s: number) => s > 0);
    const baseHour =
      openingHours?.open !== undefined && firstNonZeroIndex >= 0 ? openingHours.open - firstNonZeroIndex : 0;
    const hours = scores
      .map((score: number, index: number) => ({ hour: baseHour + index, score }))
      .filter((h: Record<string, number>) => h.score > 0);

    return {
      closeHour: openingHours?.close ?? null,
      day: dayKey,
      hours,
      openHour: openingHours?.open ?? null
    };
  });

  return {
    days,
    dwellTimeMax: popularTimes.dwellTime?.max ?? null,
    dwellTimeMin: popularTimes.dwellTime?.min ?? null
  };
}

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  activePoiId: string;
  sharedReportFullAccess?: boolean;
  userId: string;
};

type State = {
  isAllReportsModalOpen: boolean;
  isModalOpen: boolean;
};

export default function FootfallReport({
  activePoiId,
  country,
  entityType,
  entityId,
  sharedReportFullAccess,
  userId
}: Props) {
  const useBuyerInsights = useFeatureUser('buyer_insights_d2s');
  const [{ isModalOpen, isAllReportsModalOpen }, setState] = useState<State>({
    isAllReportsModalOpen: false,
    isModalOpen: false
  });

  const {
    data: pois = [],
    isLoading: isLoadingPois,
    status: poisStatus
  } = useQueryFetch<Poi[]>(
    ['footfall', 'poi-list', entityType, entityId, country],
    async () => await getPoiList(entityType, entityId, userId, { country })
  );

  if (poisStatus === 'error') {
    throw new Error('Failed to load the POI list for the footfall report');
  }

  const activePoi = pois.find((p: Poi) => p.id.toString() === activePoiId) ?? pois[0];

  const emptyKpiOverview: kpiOverViewType = {
    averageTicket: null,
    baseline: null,
    conversionRate: null,
    costPerVisit: 0,
    roi: 0,
    totalSpent: 0,
    uplift: null,
    upliftPct: null
  };

  const {
    data: popularTimesData,
    isLoading: isLoadingPopularTimes,
    status: popularTimesStatus
  } = useQueryFetch<PopularTimesData | null>(
    ['footfall', 'popularTimes', entityType, entityId, activePoi?.venueId],
    async () => {
      if (!activePoi?.venueId) {
        return null;
      }
      const response = await getFootfallPopularTimes(entityType, entityId, activePoi.venueId, userId);
      return mapPopularTimes(response.popularTimes);
    },
    { enabled: !!activePoi }
  );

  if (popularTimesStatus === 'error') {
    throw new Error('Failed to load popular times for the footfall report');
  }

  const {
    data: statsData,
    isLoading: isLoadingStats,
    status: statsStatus
  } = useQueryFetch<FootfallStatsResponse | null>(
    ['footfall', 'stats', entityType, entityId, activePoi?.venueId],
    async () => {
      const response = await getFootfallStats(
        entityType,
        entityId,
        userId,
        undefined,
        activePoi?.venueId ? [activePoi.venueId] : undefined
      );
      return { kpiOverview: response.kpiOverview ?? emptyKpiOverview };
    },
    { enabled: !!activePoi }
  );

  if (statsStatus === 'error') {
    throw new Error('Failed to load footfall stats for the footfall report');
  }

  const { data: lineItemData } = useQueryFetch<AggregatedFootfallLineItemDataResponse | null>(
    ['footfall', 'lineItemData', entityType, entityId, country],
    async () => await getAggregatedFootfallLineItemData(entityType, entityId, userId, country)
  );

  const handleModalOpen = (isOpen?: boolean) => {
    setState(prev => ({ ...prev, isModalOpen: isOpen ?? !prev.isModalOpen }));
  };

  const entityCountries = lineItemData?.lineItemData?.countries ?? [];
  const isLoading = isLoadingPois || isLoadingStats || isLoadingPopularTimes;

  return (
    <div className="footfallReport">
      <ReportHeader
        entityIds={[entityId]}
        entityType={entityType}
        lineItem={lineItemData?.lineItemData}
        lineItemIds={[entityId]}
        onAllReports={() => setState(prev => ({ ...prev, isAllReportsModalOpen: true }))}
        poiIds={activePoi ? [activePoi.id] : undefined}
        pois={activePoi ? [activePoi] : []}
        sharedReportFullAccess={sharedReportFullAccess}
        variant="single"
      />
      <PoiSelectionModal
        country={country}
        entityCountries={entityCountries}
        entityId={entityId}
        entityType={entityType}
        isOpen={isAllReportsModalOpen}
        mode="steps"
        onClose={() => setState(prev => ({ ...prev, isAllReportsModalOpen: false }))}
        userId={userId}
      />
      <div className="footfallReport__content">
        {isLoading ? (
          <FootfallReportSkeleton />
        ) : (
          activePoi && (
            <div className="footfallReport__grid">
              <div className="footfallReport__storeHeaderSlot">
                <StoreHeader poi={activePoi} userId={userId} />
              </div>
              <div className="footfallReport__primaryKpiSlot">
                {statsData && <KpiOverview data={statsData.kpiOverview} primaryOnly />}
              </div>
              <div className="footfallReport__chartAndSecondarySlot">
                {popularTimesData && <PopularTimes data={popularTimesData} />}
                {statsData && <KpiOverview data={statsData.kpiOverview} secondaryOnly />}
              </div>
            </div>
          )
        )}
        <div className="footfallReport__trafficRow">
          <TrafficAnalysis
            campaignEnd={lineItemData?.lineItemData.endDate ?? ''}
            campaignStart={lineItemData?.lineItemData.startDate ?? ''}
            entityId={entityId}
            entityType={entityType}
            timezone={lineItemData?.lineItemData.timezone ?? 'UTC'}
            userId={userId}
            venueIds={activePoi?.venueId ? [activePoi.venueId] : undefined}
          />
        </div>
        {useBuyerInsights && (
          <div className="footfallReport__buyerInsightsRow">
            <BuyerInsights activePoiId={activePoiId} country={country} entityId={entityId} entityType={entityType} />
          </div>
        )}
        <PoiSelectionModal
          country={country}
          currentSelectedId={activePoiId}
          entityCountries={entityCountries}
          entityId={entityId}
          entityType={entityType}
          isOpen={isModalOpen}
          onClose={() => handleModalOpen(false)}
          userId={userId}
        />
      </div>
    </div>
  );
}
